import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Notice } from '../../../../shared/sso-workspace/fleet-page'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { serverMessage } from '../../../../shared/sso-workspace/format'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { getConnectProviders, listConnections, reconnect, startConnect } from './alert-routing-api'
import { leaveFor, savePending } from './connect-pending'
import { CONNECTIONS_PAGE } from './paths'

// A connection's status in words, and its pill's tone. needs_approval is the
// admin's own: a connected workspace that hasn't approved every permission
// EducationXR now asks for (auth's missingScopes). It still posts; one
// Reconnect approves the rest.
export const CONNECTION_STATUS = {
  connected: { label: 'Connected', tone: 'ok' },
  needs_approval: { label: 'Needs approval', tone: 'warn' },
  needs_reconnect: { label: 'Needs reconnecting', tone: 'bad' },
  disconnected: { label: 'Disconnected', tone: 'warn' }
}
export const statusOf = status => CONNECTION_STATUS[status] || { label: status || 'Unknown', tone: null }

// The consent scopes a connected workspace hasn't approved (auth's
// missingScopes; an older auth sends none: nothing to show).
export const missingScopesOf = connection => (connection && Array.isArray(connection.missingScopes) ? connection.missingScopes : [])

// The one place a connection's state is read (owner 2026-10-30: "Connected"
// while Slack asked to re-approve): { key, label, tone }, its status with the
// missing permissions folded in.
export const connectionState = connection => {
  const status = connection && connection.status
  const key = status === 'connected' && missingScopesOf(connection).length > 0 ? 'needs_approval' : status
  return { key, ...statusOf(key) }
}
export const newPermissionsText = n => `${n} new permission${n === 1 ? '' : 's'}`

// connection: the view, so a missing approval shows; status alone for a
// caller that only has the status.
export const ConnectionPill = ({ connection, status, tone }) => {
  const s = connection ? connectionState(connection) : statusOf(status)
  return <Pill tone={tone || s.tone}>{s.label}</Pill>
}

const capitalized = s => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '')
// The provider's name as the server says it ("Slack"), else its key.
export const providerLabel = (provider, providers) => ((providers || []).find(x => x.provider === provider) || {}).label || capitalized(provider)
export const notConfiguredText = label => `${label} isn't set up on this environment yet.`
export const notReadyText = label => `${label} targets can't be used yet: every service has to be updated first.`

// Leave for the provider's consent page: ask for it, keep what the person
// was doing (connect-pending.js), then go. The page is gone after that, so
// busy stays on.
export const useLeaveForConsent = () => {
  const [busy, setBusy] = useState(false)
  const [error, failure, setError] = useErrorLine()
  const leave = async (ask, record, fallback) => {
    setBusy(true)
    setError(null)
    try {
      const { authorizeURL } = await ask()
      savePending(record)
      leaveFor(authorizeURL)
    } catch (err) {
      setError(serverMessage(err, fallback), err)
      setBusy(false)
    }
  }
  return { busy, error, failure, leave }
}

// A ConfigSchema `connection` field (Connect types: slack_app): the
// connected workspace a target posts through. None yet: Connect <provider>
// (the consent page, and back to this form with what was typed). One or
// more: pick one, with its status, and Reconnect one that is not connected.
// Not set up on this environment, or the type not ready yet (the fleet
// flag): a notice, and nothing to press.
//
// inline: the route editor's new target. Leaving the page would lose the
// unsaved route, so it never starts a consent: it says to connect (or
// reconnect) on the Alert connections page first.
// pending: { returnTo, draft } for the way back.
export const ConnectionField = ({ id, field, value, onChange, invalid, editable = true, inline = false, ready = true, pending = {} }) => {
  const [providers, setProviders] = useState(null)
  const [connections, setConnections] = useState(null)
  const [loadError, loadFailure, setLoadError] = useErrorLine()
  const consent = useLeaveForConsent()
  const picked = useRef(false)

  useEffect(() => {
    let live = true
    Promise.all([getConnectProviders(), listConnections()]).then(([p, c]) => {
      if (!live) return
      setLoadError(null)
      setProviders(p || [])
      setConnections((c || []).filter(x => x.provider === field.provider))
    }, err => live && setLoadError(serverMessage(err, "The connected workspaces couldn't be loaded."), err))
    return () => { live = false }
  }, [field.provider, setLoadError])

  // One connected workspace and nothing picked yet: pick it, once.
  const empty = value === undefined || value === null || value === ''
  useEffect(() => {
    if (picked.current || !connections || !empty || !editable) return
    picked.current = true
    if (connections.length === 1 && connections[0].status === 'connected') onChange(connections[0].id)
  }, [connections, empty, editable, onChange])

  const label = providerLabel(field.provider, providers)
  const provider = (providers || []).find(x => x.provider === field.provider)
  const configured = Boolean(provider && provider.configured)
  const record = purpose => ({ provider: field.provider, purpose, returnTo: pending.returnTo, draft: { ...(pending.draft || {}), connectionKey: field.key } })
  const toConnections = <Link to={CONNECTIONS_PAGE}>Go to Alert connections</Link>
  const failed = consent.error && <><p className="text-danger small m-0" role="alert">{consent.error}</p><ErrorReference error={consent.failure} compact /></>
  const heading = <div className={`sw-label${field.required ? ' ar-required' : ''}`}>{field.label}</div>

  if (loadError) return <>{heading}<p className="text-danger small m-0" role="alert">{loadError}</p><ErrorReference error={loadFailure} compact /></>
  if (!connections) return <>{heading}<p className="sw-hint m-0">Loading workspaces…</p></>

  // Why nothing can be connected here, if anything stops it.
  const blocked = !configured ? notConfiguredText(label) : !ready ? notReadyText(label) : null

  if (connections.length === 0) {
    return (
      <>
        {heading}
        {blocked
          ? <Notice tone="warn">{blocked}</Notice>
          : inline
            ? <Notice tone="info"><span>{`Connect ${label} on the Alert connections page first.`}</span> {toConnections}</Notice>
            : editable
              ? <div className="ar-row">
                  <button type="button" className="btn sw-btn-primary" disabled={consent.busy}
                    onClick={() => consent.leave(() => startConnect(field.provider, { scope: 'platform' }), record('connect'), `${label} couldn't be reached. Try again.`)}>
                    {`Connect ${label}`}
                  </button>
                  <span className="sw-hint">{`You'll approve it in ${label}, then come back here.`}</span>
                </div>
              : <p className="sw-hint m-0">{`No ${label} workspace is connected yet.`}</p>}
        {failed}
      </>
    )
  }

  const chosen = connections.find(c => String(c.id) === String(value))
  const notConnected = chosen && chosen.status !== 'connected'
  // still posts, so it stays a choice; Reconnect approves the rest
  const needsApproval = chosen && connectionState(chosen).key === 'needs_approval'
  return (
    <>
      <label className={`sw-label${field.required ? ' ar-required' : ''}`} htmlFor={id}>{field.label}</label>
      <div className="ar-row">
        <select id={id} className={`form-select${invalid ? ' is-invalid' : ''}`} value={empty ? '' : String(value)}
          onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))}>
          <option value="">Pick a workspace…</option>
          {connections.map(c => {
            const state = connectionState(c)
            return <option key={c.id} value={String(c.id)}>{state.key === 'connected' ? c.externalName : `${c.externalName} (${state.label.toLowerCase()})`}</option>
          })}
        </select>
        {chosen && <ConnectionPill connection={chosen} />}
        {(notConnected || needsApproval) && editable && !inline &&
          <button type="button" className="btn sw-btn" disabled={consent.busy}
            onClick={() => consent.leave(() => reconnect(chosen.id), record('reconnect'), `${label} couldn't be reached. Try again.`)}>Reconnect</button>}
      </div>
      {notConnected && chosen.statusReason && <p className="sw-hint m-0">{chosen.statusReason}</p>}
      {notConnected && inline && <p className="sw-hint m-0"><span>Reconnect it on the Alert connections page first.</span> {toConnections}</p>}
      {!configured || !ready ? <Notice tone="warn">{blocked}</Notice> : null}
      {failed}
    </>
  )
}
